import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, ArrowRight, Target, Briefcase, GraduationCap, Calendar, Clock } from 'lucide-react';
import { apiCall } from './api';
import { toast } from 'sonner';

const GOALS = [
  { id: 'speak',         icon: Target,        label: 'Parler anglais',         desc: 'Améliorer ma fluence et ma confiance.' },
  { id: 'interview',     icon: Briefcase,     label: 'Réussir un interview',   desc: 'Me préparer à un entretien professionnel.' },
  { id: 'learn-skill',   icon: GraduationCap, label: 'Apprendre une compétence', desc: 'Acquérir un savoir-faire utilisable au travail.' },
];
const LEVELS = [
  { id: 'beginner',     label: 'Débutant',       icon: '🟢' },
  { id: 'intermediate', label: 'Intermédiaire',  icon: '🟠' },
  { id: 'advanced',     label: 'Avancé',         icon: '🔵' },
];
const MINUTES = [5, 10, 15, 30];
const TIMELINES = [
  { id: 30,  label: 'Parler en 30 jours',   desc: 'Intensif' },
  { id: 60,  label: 'Parler en 60 jours',   desc: 'Modéré' },
  { id: 90,  label: 'Parler en 90 jours',   desc: 'Relaxé' },
  { id: 0,   label: 'À mon rythme',         desc: 'Sans pression' },
];

export default function Onboarding({ token, onDone }) {
  const [step, setStep] = useState(0);
  const [goal, setGoal] = useState(null);
  const [level, setLevel] = useState(null);
  const [mins, setMins] = useState(10);
  const [target, setTarget] = useState(60);
  const [estimate, setEstimate] = useState(null);
  const [loading, setLoading] = useState(false);

  // Live estimate when minutes or target change
  useEffect(() => {
    if (step !== 3) return;
    apiCall('/estimate', 'POST', { daily_minutes: mins, target_days: target })
      .then(setEstimate).catch(() => {});
  }, [mins, target, step]);

  const finish = async () => {
    setLoading(true);
    try {
      await apiCall('/onboarding', 'POST', {
        goal, level_self: level, daily_minutes: mins,
      }, token);
      toast.success('Parcours personnalisé !');
      onDone({ goal, level_self: level, daily_minutes: mins, target_days: target });
    } catch (e) { toast.error(e.message); }
    setLoading(false);
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-xl mx-auto px-4 py-12">
      <div className="bg-gradient-to-br from-emerald-900/40 to-slate-900 border border-emerald-500/30 rounded-3xl p-8" data-testid="academy-onboarding">
        <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-3 py-1 text-xs text-emerald-300 mb-4"><Sparkles size={12} />Configurons ton parcours</div>
        <div className="flex gap-2 mb-6">
          {[0,1,2,3].map(s => <div key={s} className={`h-1.5 flex-1 rounded-full ${s <= step ? 'bg-emerald-400' : 'bg-white/10'}`} />)}
        </div>

        {step === 0 && (
          <div>
            <h2 className="text-2xl font-bold mb-1">Quel est ton objectif ?</h2>
            <p className="text-white/60 text-sm mb-6">On adaptera tes leçons en conséquence.</p>
            <div className="space-y-3">
              {GOALS.map(g => (
                <button key={g.id} onClick={() => setGoal(g.id)} data-testid={`onb-goal-${g.id}`}
                  className={`w-full text-left p-4 rounded-xl border-2 transition flex items-center gap-3 ${goal === g.id ? 'border-emerald-400 bg-emerald-500/10' : 'border-white/10 bg-white/5 hover:border-white/20'}`}>
                  <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center"><g.icon size={20} className="text-emerald-300" /></div>
                  <div>
                    <div className="font-bold text-sm">{g.label}</div>
                    <div className="text-xs text-white/60">{g.desc}</div>
                  </div>
                </button>
              ))}
            </div>
            <button disabled={!goal} onClick={() => setStep(1)} data-testid="onb-next-1" className="mt-6 w-full bg-emerald-500 hover:bg-emerald-600 disabled:opacity-40 text-slate-950 font-bold py-3 rounded-xl flex items-center justify-center gap-2">Continuer<ArrowRight size={16} /></button>
          </div>
        )}

        {step === 1 && (
          <div>
            <h2 className="text-2xl font-bold mb-1">Quel est ton niveau actuel ?</h2>
            <p className="text-white/60 text-sm mb-6">Tu pourras le modifier plus tard.</p>
            <div className="grid grid-cols-3 gap-3">
              {LEVELS.map(l => (
                <button key={l.id} onClick={() => setLevel(l.id)} data-testid={`onb-level-${l.id}`}
                  className={`p-4 rounded-xl border-2 transition text-center ${level === l.id ? 'border-emerald-400 bg-emerald-500/10' : 'border-white/10 bg-white/5 hover:border-white/20'}`}>
                  <div className="text-3xl mb-1">{l.icon}</div>
                  <div className="text-xs font-bold">{l.label}</div>
                </button>
              ))}
            </div>
            <div className="flex gap-2 mt-6">
              <button onClick={() => setStep(0)} className="px-4 py-3 text-white/60 hover:text-white text-sm">Retour</button>
              <button disabled={!level} onClick={() => setStep(2)} data-testid="onb-next-2" className="flex-1 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-40 text-slate-950 font-bold py-3 rounded-xl flex items-center justify-center gap-2">Continuer<ArrowRight size={16} /></button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div>
            <h2 className="text-2xl font-bold mb-1 flex items-center gap-2"><Calendar size={22} className="text-emerald-300" />Ton objectif temps</h2>
            <p className="text-white/60 text-sm mb-6">Combien de temps veux-tu mettre pour parler anglais ?</p>
            <div className="grid grid-cols-2 gap-2">
              {TIMELINES.map(t => (
                <button key={t.id} onClick={() => setTarget(t.id)} data-testid={`onb-target-${t.id}`}
                  className={`p-3 rounded-xl border-2 transition text-left ${target === t.id ? 'border-emerald-400 bg-emerald-500/10' : 'border-white/10 bg-white/5 hover:border-white/20'}`}>
                  <div className="font-bold text-sm">{t.label}</div>
                  <div className="text-[11px] text-white/60">{t.desc}</div>
                </button>
              ))}
            </div>
            <div className="flex gap-2 mt-6">
              <button onClick={() => setStep(1)} className="px-4 py-3 text-white/60 hover:text-white text-sm">Retour</button>
              <button onClick={() => setStep(3)} data-testid="onb-next-3" className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-bold py-3 rounded-xl flex items-center justify-center gap-2">Continuer<ArrowRight size={16} /></button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div>
            <h2 className="text-2xl font-bold mb-1 flex items-center gap-2"><Clock size={22} className="text-emerald-300" />Combien de minutes par jour ?</h2>
            <p className="text-white/60 text-sm mb-6">L'estimation s'ajuste en direct.</p>
            <div className="grid grid-cols-4 gap-2">
              {MINUTES.map(m => (
                <button key={m} onClick={() => setMins(m)} data-testid={`onb-mins-${m}`}
                  className={`p-3 rounded-xl border-2 transition ${mins === m ? 'border-emerald-400 bg-emerald-500/10' : 'border-white/10 bg-white/5 hover:border-white/20'}`}>
                  <div className="font-bold">{m} min</div>
                </button>
              ))}
            </div>

            {estimate && (
              <div className="mt-5 bg-white/5 border border-white/10 rounded-xl p-4 text-sm" data-testid="onb-estimation">
                <div className="text-emerald-300 font-bold mb-1">Estimation pour finir tous les cours</div>
                <div className="text-white/80">{estimate.total_lessons} leçons · ~{estimate.total_hours} h de contenu</div>
                <div className="text-white/80 mt-1">À <strong>{mins} min/jour</strong> → environ <strong className="text-yellow-300">{estimate.days_needed_at_pace} jours</strong></div>
                {estimate.feasibility && target > 0 && (
                  <div className={`mt-3 p-2.5 rounded-lg text-xs ${estimate.feasibility.achievable_with_current_pace ? 'bg-emerald-500/15 text-emerald-300' : 'bg-yellow-500/15 text-yellow-300'}`}>
                    {estimate.feasibility.achievable_with_current_pace
                      ? `✓ À ${mins} min/jour, tu finiras avant tes ${target} jours`
                      : `⚠ Pour finir en ${target} jours, il te faudrait ${estimate.feasibility.required_minutes_per_day} min/jour. Tu peux ajuster.`}
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-2 mt-6">
              <button onClick={() => setStep(2)} className="px-4 py-3 text-white/60 hover:text-white text-sm">Retour</button>
              <button disabled={loading} onClick={finish} data-testid="onb-finish" className="flex-1 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-40 text-slate-950 font-bold py-3 rounded-xl flex items-center justify-center gap-2">Commencer mon parcours<ArrowRight size={16} /></button>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
}
